import { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, useRouteError } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import { Shell } from '@/components/Shell';
import { queryClient } from '@/lib/api';
import { cx } from '@/lib/cx';
import type { Theme } from '@/lib/theme';
import { initialTheme, saveTheme } from '@/lib/theme';
import { AccountsPage } from '@/pages/Accounts';
import { AskPage } from '@/pages/Ask';
import { CompanyPage } from '@/pages/Company';
import { CostPage } from '@/pages/Cost';
import { DemandPage } from '@/pages/Demand';
import { DigestsPage } from '@/pages/Digests';
import { FeaturesPage } from '@/pages/Features';
import { FeedPage } from '@/pages/Feed';
import { IssuesPage } from '@/pages/Issues';
import { JourneysPage } from '@/pages/Journeys';
import { NotFoundPage } from '@/pages/NotFound';
import { OverviewPage } from '@/pages/Overview';
import { UserPage } from '@/pages/User';

function Root() {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  useEffect(() => {
    document.documentElement.style.colorScheme = theme;
    document.body.style.background = theme === 'dark' ? '#0f1420' : '#f9fafb';
  }, [theme]);
  const choose = (next: Theme) => {
    saveTheme(next);
    setTheme(next);
  };
  return (
    <div className={cx('cu', theme === 'dark' && 'dk')}>
      <Shell theme={theme} onTheme={choose} />
    </div>
  );
}

function RouteError() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className={cx('cu', initialTheme() === 'dark' && 'dk')}>
      <div className="page">
        <div className="card empty">
          <div className="ic">⚠</div>
          <div style={{ fontWeight: 700, color: 'var(--ink)' }}>Something broke on this page</div>
          <div style={{ overflowWrap: 'anywhere' }}>{message}</div>
          <a className="btn" href="/">
            Back to Overview
          </a>
        </div>
      </div>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: 'feed', element: <FeedPage /> },
      { path: 'issues', element: <IssuesPage /> },
      { path: 'issues/:issueId', element: <IssuesPage /> },
      { path: 'demand', element: <DemandPage /> },
      { path: 'features', element: <FeaturesPage /> },
      { path: 'features/:area', element: <FeaturesPage /> },
      { path: 'accounts', element: <AccountsPage /> },
      { path: 'accounts/:companyId', element: <CompanyPage /> },
      { path: 'users/:userId', element: <UserPage /> },
      { path: 'cost', element: <CostPage /> },
      { path: 'journeys', element: <JourneysPage /> },
      { path: 'ask', element: <AskPage /> },
      { path: 'digests', element: <DigestsPage /> },
      { path: 'digests/:kind/:start', element: <DigestsPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </QueryClientProvider>
  );
}
